import { handleApiResponse } from 'utilities/apiUtils';
import { mergeZips, countZipEntries, readSingleEntry } from 'utilities/zipMerge';
import React from 'react';
import Skeleton from 'react-loading-skeleton';
import { Paginator } from 'primereact/paginator';
import { InputText } from 'primereact/inputtext';
import { Calendar } from 'primereact/calendar';
import { Dialog } from 'primereact/dialog';
import { ProgressSpinner } from 'primereact/progressspinner';
import { Button } from 'primereact/button';
import '../../styles/sendCertificate.css';
import '../../styles/mastercertificate.css';
import ErrorState from 'components/ErrorState';
import RescheduleDialog from './components/RescheduleDialog';
import CancelScheduleDialog from './components/CancelScheduleDialog';

export type SentCertificate = {
  id: string;
  certificateHolderName: string;
  certificateName: string;
  sentDate: string;
  recipient: string;
  fax: string;
  status: string;
  additionalInterestIndicator: boolean;
  hasCertificateHolder: boolean;
  isSent: boolean;
  sentStatus: string;
  scheduledDateTime: string | null;
};

export type SentCertificatesPanelProps = {
  apiUrl: string;
  token: string;
  insuredDatabaseId: string;
};

function formatDate(date: Date) {
  return date.toISOString().slice(0, 10);
}

function formatUsPhone(value: string): string {
  const digits = value.replace(/\D/g, '');
  if (digits.length === 10) return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
  if (digits.length === 11 && digits[0] === '1') return `(${digits.slice(1, 4)}) ${digits.slice(4, 7)}-${digits.slice(7)}`;
  return value;
}

/**
 * Page sizes the agent can pick from, smallest first, which is also the default.
 *
 * The panel used to be fixed at seven rows. An insured with a hundred additional interests has
 * hundreds of sent certificates, and selecting them seven at a time is a lot of paging for
 * someone whose whole intent is to take the lot.
 */
const ROWS_PER_PAGE_OPTIONS = [10, 20, 50];

/**
 * How far the table is padded with blank rows when a page is short.
 *
 * Without this a page holding one certificate collapses to a single line. Padding all the way
 * to the page size would be worse at the larger sizes, where a short last page would trail
 * forty empty rows, so it stops once the table looks like a table.
 */
const MIN_PADDED_ROWS = 10;

// Certificates per request to download-zip. There is no limit on how many an agent may
// select: the selection is split into requests of this size, fetched one after another, and
// the archives stitched back into a single file before it is handed over.
//
// A hundred took 54.8 seconds against stage, which sits comfortably inside the 230 seconds
// Azure App Service will hold a response open, and leaves room for the retries the API now
// makes. Asking for the whole selection in one request is what does not scale: a request for
// 244 came back with 143 of them and an HTTP 200.
const BULK_CHUNK_SIZE = 100;

// Selections at or above this are told the wait is expected. Ten certificates came back in
// under six seconds against stage where a hundred took nearly a minute, and nothing on screen
// moves while the zip is being built, so without a word here the reasonable conclusion is
// that the button did nothing and the sensible reaction is to click it again.
const BULK_SLOW_HINT = 10;

/**
 * What each certificate is called inside the archive.
 *
 * The API falls back to naming entries after the certificate holder alone, and a holder
 * normally has many certificates: one insured on stage has 237 certificates belonging to 7
 * holders, which arrive as `Bohdan Sakalo (2)` through `Bohdan Sakalo (108)`. Nothing in those
 * names says which certificate is which.
 *
 * So the holder, the certificate and the date it went out are all sent, which also makes the
 * name unique and keeps the API from having to number anything. The same shape as the
 * Send Certificate flow's own zip download, with the date added.
 */
function certificateLabel(cert: SentCertificate): string {
  const holder = cert.certificateHolderName && cert.certificateHolderName !== '-'
    ? cert.certificateHolderName
    : 'No certificate holder';
  const parts = [holder, cert.certificateName, cert.sentDate].filter(
    part => part && part !== '-',
  );
  // Characters a file name cannot hold, dropped here rather than by the API, which replaces
  // them silently and can collapse two different names into one. The comma between a date and
  // its time is removed rather than substituted, so it does not leave a stray dash behind.
  return parts
    .join(' - ')
    .replace(/,/g, '')
    .replace(/[/\\?%*:|"<>]/g, '-')
    .replace(/\s+/g, ' ')
    .trim();
}

const SentCertificatesPanel: React.FC<SentCertificatesPanelProps> = ({ apiUrl, insuredDatabaseId }) => {
  const [certificates, setCertificates] = React.useState<SentCertificate[]>([]);
  const [page, setPage] = React.useState(0);
  const [rowsPerPage, setRowsPerPage] = React.useState(ROWS_PER_PAGE_OPTIONS[0]);
  const [totalRecords, setTotalRecords] = React.useState(0);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState<string | null>(null);
  const [fromDate, setFromDate] = React.useState<Date>(() => {
    const d = new Date();
    d.setDate(d.getDate() - 7);
    return d;
  });
  const [toDate, setToDate] = React.useState<Date>(() => new Date());
  const [search, setSearch] = React.useState('');
  const [previewDialog, setPreviewDialog] = React.useState<{ visible: boolean; url: string | null; loading: boolean }>({
    visible: false,
    url: null,
    loading: false,
  });

  const [rescheduleDialog, setRescheduleDialog] = React.useState<{
    visible: boolean;
    certId: string;
    initialDateTime: string | null;
  }>({ visible: false, certId: '', initialDateTime: null });

  const openRescheduleDialog = (cert: SentCertificate) => {
    setRescheduleDialog({ visible: true, certId: cert.id, initialDateTime: cert.scheduledDateTime });
  };

  const closeRescheduleDialog = () => {
    setRescheduleDialog({ visible: false, certId: '', initialDateTime: null });
  };

  // Keyed by id rather than by row, so a selection survives paging: an insured with a hundred
  // interests spreads its certificates over many pages, and downloading them is the whole point.
  const [selected, setSelected] = React.useState<Record<string, string>>({});
  const [bulkBusy, setBulkBusy] = React.useState(false);
  // Which chunk is in flight, so a multi-minute download can say it is still moving.
  const [bulkProgress, setBulkProgress] = React.useState<{ done: number; total: number } | null>(null);
  const selectedIds = Object.keys(selected);

  const toggleRow = (cert: SentCertificate) =>
    setSelected(prev => {
      const next = { ...prev };
      if (next[cert.id]) delete next[cert.id];
      else next[cert.id] = certificateLabel(cert);
      return next;
    });

  const pageAllSelected = certificates.length > 0 && certificates.every(c => selected[c.id]);

  const togglePage = () =>
    setSelected(prev => {
      const next = { ...prev };
      if (pageAllSelected) certificates.forEach(c => delete next[c.id]);
      else certificates.forEach(c => { next[c.id] = certificateLabel(c); });
      return next;
    });

  const [cancelDialog, setCancelDialog] = React.useState<{ visible: boolean; certId: string; certificateName: string }>({ visible: false, certId: '', certificateName: '' });

  const openCancelDialog = (cert: SentCertificate) => {
    setCancelDialog({ visible: true, certId: cert.id, certificateName: cert.certificateName });
  };

  const closeCancelDialog = () => {
    setCancelDialog({ visible: false, certId: '', certificateName: '' });
  };

  const fetchCertificates = React.useCallback(async (pageNum: number, rows: number = rowsPerPage) => {
    setLoading(true);
    setError(null);
    try {
      const ncToken = localStorage.getItem('NCToken') || '';
      const authToken = localStorage.getItem('igtoken') || '';
      const skip = pageNum * rows;
      let url = `${apiUrl}?top=${rows}&skip=${skip}&fromDate=${formatDate(fromDate)}&toDate=${formatDate(toDate)}&insuredDatabaseId=${encodeURIComponent(insuredDatabaseId)}`;
      if (search) url += `&certificateName=${encodeURIComponent(search)}`;
      const res = await fetch(url, {
        headers: {
          'NCToken': ncToken,
          'Accept': 'application/json',
          'Authorization': `Bearer ${authToken}`,
        },
      });
      await handleApiResponse(res);
      const data = await res.json();
      setTotalRecords(data['@odata.count'] ?? 0);
      const certs = (data.value || []).map((item: any) => ({
        id: item.databaseId,
        certificateHolderName: item.certificateHolderName || item.name || '-',
        certificateName: item.certificateName || '-',
        sentDate: item.createDate ? new Date(item.createDate).toLocaleString('en-US', { month: '2-digit', day: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit', hour12: true }) : '-',
        recipient: item.eMail || '',
        fax: item.fax || '',
        status: item.type || '-',
        additionalInterestIndicator: !!item.additionalInterestIndicator,
        hasCertificateHolder: !!(item.certificateHolderName && item.certificateHolderName.trim()),
        isSent: !!item.isSent,
        sentStatus: item.status || '',
        scheduledDateTime: item.scheduledDateTime ?? null,
      }));
      setCertificates(certs);
    } catch (err: any) {
      setError(err.message || 'Error fetching sent certificates');
    } finally {
      setLoading(false);
    }
  }, [apiUrl, insuredDatabaseId, fromDate, toDate, search, rowsPerPage]);

  // Reset to page 0 and fetch when filters change
  React.useEffect(() => {
    setPage(0);
    fetchCertificates(0);
  }, [fromDate, toDate, search, insuredDatabaseId]);

  const fetchPreviewDownloadUrls = async (certificateId: string) => {
    try {
      const ncToken = localStorage.getItem('NCToken') || '';
      const authToken = localStorage.getItem('igtoken') || '';
      const url = `${process.env.REACT_APP_COIPortal_Url}/api/IGIGCOI/download-preview?certificateId=${encodeURIComponent(certificateId)}`;
      const res = await fetch(url, {
        headers: {
          'NCToken': ncToken,
          'Accept': 'application/json',
          'Authorization': `Bearer ${authToken}`,
        },
      });
      await handleApiResponse(res);
      const data = await res.json();
      return data;
    } catch (err: any) {
      throw new Error(err.message || 'Error fetching preview/download URLs');
    }
  };

  const handlePreview = async (certificateId: string) => {
    setPreviewDialog({ visible: true, url: null, loading: true });
    try {
      const data = await fetchPreviewDownloadUrls(certificateId);
      if (data.success && data.previewUrl) {
        setPreviewDialog({ visible: true, url: data.previewUrl, loading: false });
      } else {
        throw new Error('Preview URL not available');
      }
    } catch (err: any) {
      setError(err.message || 'Error loading preview');
      setPreviewDialog({ visible: false, url: null, loading: false });
    }
  };

  // One request per row cost a round trip each — about three and a half seconds against stage —
  // so twenty certificates meant twenty clicks and over a minute of waiting. One request for
  // the whole selection does not work either: a request for 244 came back with 143 of them.
  // So the selection is split, fetched a chunk at a time, and put back together here. The
  // agent asked for their certificates, not for a pile of archives to reassemble themselves.
  const handleBulkDownload = async () => {
    if (selectedIds.length === 0 || bulkBusy) return;

    const chunks: string[][] = [];
    for (let i = 0; i < selectedIds.length; i += BULK_CHUNK_SIZE) {
      chunks.push(selectedIds.slice(i, i + BULK_CHUNK_SIZE));
    }

    setBulkBusy(true);
    setBulkProgress({ done: 0, total: chunks.length });
    setError(null);

    const ncToken = localStorage.getItem('NCToken') || '';
    const authToken = localStorage.getItem('igtoken') || '';
    const archives: ArrayBuffer[] = [];
    let firstFailure: string | null = null;

    try {
      // One after another rather than all at once. Running them together would rebuild the
      // same stampede at the AMS that splitting the request was meant to avoid.
      for (const chunk of chunks) {
        try {
          const res = await fetch(`${process.env.REACT_APP_COIPortal_Url}/api/IGIGCOI/download-zip`, {
            method: 'POST',
            headers: {
              'NCToken': ncToken,
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${authToken}`,
            },
            body: JSON.stringify({
              certificates: chunk.map(id => ({ id, name: selected[id] })),
            }),
          });
          await handleApiResponse(res);
          archives.push(await res.arrayBuffer());
        } catch (err: any) {
          // A rejected session is fatal and has already cleared the login, so stop. Anything
          // else costs this chunk only, and the rest of the selection is still worth fetching.
          if (err?.message === 'Unauthorized') throw err;
          if (!firstFailure) firstFailure = err?.message || null;
        }
        setBulkProgress(prev => (prev ? { ...prev, done: prev.done + 1 } : prev));
      }

      if (archives.length === 0) {
        throw new Error(firstFailure || 'Could not download the selected certificates');
      }

      // One certificate is handed over as itself. Wrapping a single file in an archive only
      // makes the agent unpack it, and the entry already carries a better name than anything
      // that could be invented here. If it cannot be unpacked the archive is saved instead,
      // since an unexpected .zip beats no download.
      const single = selectedIds.length === 1 && archives.length === 1
        ? await readSingleEntry(archives[0])
        : null;

      const payload = single
        ? { bytes: single.bytes, type: 'application/pdf', name: single.name }
        : {
            bytes: mergeZips(archives),
            type: 'application/zip',
            name: `certificates-${formatDate(new Date())}.zip`,
          };

      const link = document.createElement('a');
      link.href = URL.createObjectURL(new Blob([payload.bytes], { type: payload.type }));
      link.download = payload.name;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(link.href);

      // Say so when some certificates did not make it, rather than letting a short archive
      // pass for a complete one. This counts what actually arrived, so it covers both a chunk
      // that failed outright and certificates the API could not fetch within a chunk.
      const got = archives.reduce((sum, buf) => sum + (countZipEntries(buf) ?? 0), 0);
      if (got < selectedIds.length) {
        setError(`Downloaded ${got} of ${selectedIds.length} certificates. The rest could not be retrieved. Your selection has been kept, so you can try again.`);
      } else {
        setSelected({});
      }
    } catch (err: any) {
      setError(err?.message || 'Could not download the selected certificates');
    } finally {
      setBulkBusy(false);
      setBulkProgress(null);
    }
  };

  const handleDownload = async (certificateId: string) => {
    try {
      const data = await fetchPreviewDownloadUrls(certificateId);
      if (data.success && data.downloadUrl) {
        const link = document.createElement('a');
        link.href = data.downloadUrl;
        link.download = `certificate-${certificateId}.pdf`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      } else {
        throw new Error('Download URL not available');
      }
    } catch (err: any) {
      setError(err.message || 'Error downloading certificate');
    }
  };

  return (
    <div className="sentCertPanelOuter" style={{ display: 'flex', flexDirection: 'column', height: '100%', width: '100%' }}>
      <div style={{ padding: '10px 14px 8px', flex: '0 0 auto', borderBottom: '1px solid #f1f5f9' }}>
        <div style={{ display: 'flex', alignItems: 'flex-end', gap: 10, flexWrap: 'wrap' }}>
          {/* Date From */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            <label htmlFor="fromDate" style={{ fontSize: 11, fontWeight: 500, color: '#64748b', letterSpacing: '0.01em' }}>Date from</label>
            <Calendar
              id="fromDate"
              value={fromDate}
              onChange={e => setFromDate(e.value as Date)}
              dateFormat="mm/dd/yy"
              placeholder="From"
              showIcon
              style={{ width: 148 }}
              inputStyle={{ height: 32, fontSize: 12, padding: '0 8px' }}
            />
          </div>
          {/* Date To */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            <label htmlFor="toDate" style={{ fontSize: 11, fontWeight: 500, color: '#64748b', letterSpacing: '0.01em' }}>Date to</label>
            <Calendar
              id="toDate"
              value={toDate}
              onChange={e => setToDate(e.value as Date)}
              dateFormat="mm/dd/yy"
              placeholder="To"
              showIcon
              style={{ width: 148 }}
              inputStyle={{ height: 32, fontSize: 12, padding: '0 8px' }}
            />
          </div>
          {/* Search */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 3, flex: 1, minWidth: 180 }}>
            <label htmlFor="searchCert" style={{ fontSize: 11, fontWeight: 500, color: '#64748b', letterSpacing: '0.01em' }}>Search</label>
            <span className="p-input-icon-left" style={{ width: '100%' }}>
              <i className="pi pi-search" style={{ fontSize: 11, color: '#94a3b8', left: 9 }} />
              <InputText
                id="searchCert"
                value={search}
                onChange={e => setSearch(e.target.value)}
                placeholder="Search sent certificates..."
                style={{ width: '100%', height: 32, fontSize: 12, paddingLeft: 28, border: '1px solid #e2e8f0', borderRadius: 6, boxShadow: 'none' }}
              />
            </span>
          </div>
          {/* Refresh */}
          <button
            type="button"
            title="Refresh"
            onClick={() => fetchCertificates(page)}
            disabled={loading}
            className="sentCertPanel__refreshBtn"
            style={{ height: 32, width: 32, minWidth: 32 }}
          >
            <i className={`pi pi-refresh${loading ? ' pi-spin' : ''}`} style={{ fontSize: 12 }} />
          </button>
        </div>
      </div>
      {loading ? (
        <div style={{ padding: '0 10px 10px', flex: 1, minHeight: 0, overflowY: 'auto' }}>
          <table className="sentCertPanel__table" style={{ minWidth: 1000 }}>
            <thead className="sentCertPanel__thead">
              <tr>
                <th style={{ width: '20%' }}>Certificate holder</th>
                <th style={{ width: '24%' }}>Certificate</th>
                {/* <th style={{ width: '9%' }}>Status</th> */}
                <th style={{ width: '10%' }}>Sent</th>
                <th style={{ width: '16%' }}>Email / Fax</th>
                <th style={{ width: '13%' }}>Date</th>
                <th style={{ width: '8%' }}>Actions</th>
              </tr>
            </thead>
            <tbody className="sentCertPanel__tbody">
              {Array.from({ length: 5 }).map((_, i) => (
                <tr key={i}>
                  <td><Skeleton width="75%" height={12} /></td>
                  <td><Skeleton width="85%" height={12} /></td>
                  <td><Skeleton width={60} height={22} borderRadius={20} /></td>
                  <td><Skeleton width={50} height={22} borderRadius={20} /></td>
                  <td><Skeleton width="80%" height={12} /></td>
                  <td><Skeleton width="70%" height={12} /></td>
                  <td>
                    <div style={{ display: 'flex', gap: 4 }}>
                      <Skeleton width={26} height={26} borderRadius={6} />
                      <Skeleton width={26} height={26} borderRadius={6} />
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : error ? (
        <ErrorState compact />
      ) : certificates.length === 0 ? (
        <div className="panel__emptyState">
          <div className="panel__emptyState__iconWrap">
            <i className="pi pi-send panel__emptyState__icon" />
          </div>
          <div className="panel__emptyState__title">No sent certificates found</div>
          <div className="panel__emptyState__sub">Try adjusting the date range or search, or send your first certificate.</div>
        </div>
      ) : (
        <>
          {/* Set a fixed minHeight for the table container to keep paginator position consistent */}
          <div
            className="sentCertPanel__tableWrap"
            style={{
              flex: 1,
              overflow: 'auto',
              minHeight: 0,
              padding: '0 10px 10px',
            }}
          >
            <table className="sentCertPanel__table" style={{ minWidth: 1000 }}>
              <thead className="sentCertPanel__thead">
                <tr>
                  <th style={{ width: '3%', paddingRight: 0 }}>
                    <input
                      type="checkbox"
                      aria-label="Select all certificates on this page"
                      title="Select all on this page"
                      checked={pageAllSelected}
                      onChange={togglePage}
                      disabled={certificates.length === 0}
                      style={{ cursor: 'pointer' }}
                    />
                  </th>
                  <th style={{ width: '20%' }}>Certificate holder</th>
                  <th style={{ width: '24%' }}>Certificate</th>
                  {/* <th style={{ width: '9%' }}>Status</th> */}
                  <th style={{ width: '10%' }}>Sent</th>
                  <th style={{ width: '16%' }}>Email / Fax</th>
                  <th style={{ width: '13%' }}>Date</th>
                  <th style={{ width: '8%' }}>Actions</th>
                </tr>
              </thead>
              <tbody className="sentCertPanel__tbody">
                {certificates.map(cert => (
                  <tr key={cert.id}>
                    <td style={{ paddingRight: 0 }}>
                      <input
                        type="checkbox"
                        aria-label={`Select certificate for ${cert.certificateHolderName}`}
                        checked={!!selected[cert.id]}
                        onChange={() => toggleRow(cert)}
                        style={{ cursor: 'pointer' }}
                      />
                    </td>
                    <td className="sentCert__holderCell">
                      <div className="sentCert__badgeRow">
                        {(cert.hasCertificateHolder || cert.additionalInterestIndicator) && (
                          <span className="sentCert__badges">
                            {cert.hasCertificateHolder && (
                              <span className="sentCert__badge sentCert__badge--holder" title="Certificate holder">H</span>
                            )}
                            {cert.additionalInterestIndicator && (
                              <span className="sentCert__badge sentCert__badge--ai" title="Additional interest">A</span>
                            )}
                          </span>
                        )}
                        <span className="sentCert__holderName" title={cert.certificateHolderName}>
                          {cert.certificateHolderName}
                        </span>
                      </div>
                    </td>
                    <td className="sentCert__certNameCell" title={cert.certificateName}>{cert.certificateName}</td>
                    {/* <td>
                      <span className={
                        cert.status === 'Active'
                          ? 'sentCertPanel__chip sentCertPanel__chip--active'
                          : 'sentCertPanel__chip'
                      }>{cert.status}</span>
                    </td> */}
                    <td>
                      {cert.isSent ? (
                        <span className="sentCertPanel__chip sentCertPanel__chip--sent">Sent</span>
                      ) : cert.scheduledDateTime ? (
                        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                          <span style={{ display: 'inline-flex', alignItems: 'stretch', borderRadius: 999, overflow: 'hidden', border: '1px solid #c4b5fd', fontSize: 11, fontWeight: 600, whiteSpace: 'nowrap' }}>
                            <span style={{ background: '#7c3aed', color: '#fff', padding: '3px 8px', display: 'flex', alignItems: 'center', gap: 3 }}>
                              <i className="pi pi-clock" style={{ fontSize: 9 }} />
                              Scheduled
                            </span>
                            <span style={{ background: '#ede9fe', color: '#6d28d9', padding: '3px 8px', display: 'flex', alignItems: 'center' }}>
                              {new Date(cert.scheduledDateTime).toLocaleString('en-US', { month: '2-digit', day: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit', hour12: true })}
                            </span>
                          </span>
                          <i
                            className="pi pi-pencil"
                            title="Edit schedule"
                            onClick={() => openRescheduleDialog(cert)}
                            style={{ fontSize: 11, color: '#7c3aed', cursor: 'pointer', padding: 3, borderRadius: 4 }}
                          />
                          <i
                            className="pi pi-times-circle"
                            title="Cancel schedule"
                            onClick={() => openCancelDialog(cert)}
                            style={{ fontSize: 11, color: '#dc2626', cursor: 'pointer', padding: 3, borderRadius: 4 }}
                          />
                        </span>
                      ) : cert.sentStatus === 'Postponed' ? (
                        <span className="sentCertPanel__chip sentCertPanel__chip--canceled">Canceled</span>
                      ) : (
                        <span className="sentCertPanel__chip sentCertPanel__chip--notSent">Queued</span>
                      )}
                    </td>
                    <td className="sentCert__emailCell">
                      {cert.recipient && <div title={cert.recipient}>{cert.recipient}</div>}
                      {cert.fax && <div title={cert.fax}>{formatUsPhone(cert.fax)}</div>}
                      {!cert.recipient && !cert.fax && '-'}
                    </td>
                    <td className="sentCert__dateCell">{cert.sentDate}</td>
                    <td>
                      <div className="sentCert__actions">
                        <i
                          className="pi pi-eye sentCert__actionIcon sentCert__actionIcon--preview"
                          onClick={() => handlePreview(cert.id)}
                          title="Preview certificate"
                        />
                        <i
                          className="pi pi-download sentCert__actionIcon sentCert__actionIcon--download"
                          onClick={() => handleDownload(cert.id)}
                          title="Download certificate"
                        />
                      </div>
                    </td>
                  </tr>
                ))}
                {Array.from({ length: Math.max(0, Math.min(rowsPerPage, MIN_PADDED_ROWS) - certificates.length) }).map((_, idx) => (
                  <tr key={"empty-" + idx}>
                    <td colSpan={8} style={{ border: 'none', background: 'transparent' }} />
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div style={{ flex: '0 0 auto', padding: '8px 12px', borderTop: '1px solid #e6e7ef', background: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, minWidth: 0 }}>
              <Button
                label={bulkBusy
                  ? (bulkProgress && bulkProgress.total > 1
                      ? `Preparing ${bulkProgress.done}/${bulkProgress.total}...`
                      : 'Preparing...')
                  : selectedIds.length > 0
                    ? selectedIds.length === 1
                      ? 'Download 1 as .pdf'
                      : `Download ${selectedIds.length} as .zip`
                    : 'Download selected'}
                icon={bulkBusy ? 'pi pi-spin pi-spinner' : 'pi pi-download'}
                onClick={handleBulkDownload}
                disabled={selectedIds.length === 0 || bulkBusy}
                style={{ height: 32, fontSize: 12, padding: '0 12px', whiteSpace: 'nowrap' }}
              />
              {selectedIds.length > 0 && !bulkBusy && (
                <button
                  onClick={() => setSelected({})}
                  style={{ background: 'none', border: 'none', color: '#64748b', fontSize: 11, cursor: 'pointer', padding: 0 }}
                >
                  Clear
                </button>
              )}
              {bulkBusy ? (
                <small style={{ color: '#64748b', fontSize: 11 }}>
                  {bulkProgress && bulkProgress.total > 1
                    ? `Collecting batch ${Math.min(bulkProgress.done + 1, bulkProgress.total)} of ${bulkProgress.total}. `
                    : 'Building your .zip. '}
                  A large download can take a few minutes &mdash; please keep this page open.
                </small>
              ) : selectedIds.length >= BULK_SLOW_HINT ? (
                <small style={{ color: '#64748b', fontSize: 11 }}>
                  Downloading {selectedIds.length} certificates will take a
                  {selectedIds.length > BULK_CHUNK_SIZE ? ' few minutes' : ' moment'}
                </small>
              ) : null}
            </div>
            <Paginator
              rows={rowsPerPage}
              rowsPerPageOptions={ROWS_PER_PAGE_OPTIONS}
              totalRecords={totalRecords}
              first={page * rowsPerPage}
              onPageChange={e => {
                // Fires for a page change and for a page-size change alike, so the new size is
                // taken from the event and handed to the fetch rather than read back from
                // state, which has not updated yet.
                const rows = e.rows;
                const newPage = Math.floor(e.first / rows);
                if (rows !== rowsPerPage) setRowsPerPage(rows);
                setPage(newPage);
                fetchCertificates(newPage, rows);
              }}
              template={{
                layout: 'RowsPerPageDropdown PrevPageLink PageLinks NextPageLink',
                // Wrapped rather than replaced: options.element is PrimeReact's own control,
                // so it keeps its behaviour and only gains a label.
                RowsPerPageDropdown: options => (
                  <span className="sentCertPanel__pageSize">
                    <span className="sentCertPanel__pageSizeLabel">Page Size</span>
                    {options.element}
                  </span>
                ),
              }}
              className="requests-paginator p-paginator p-component"
            />
          </div>
        </>
      )}

      <CancelScheduleDialog
        visible={cancelDialog.visible}
        certId={cancelDialog.certId}
        certificateName={cancelDialog.certificateName}
        insuredDatabaseId={insuredDatabaseId}
        onHide={closeCancelDialog}
        onSuccess={() => { closeCancelDialog(); fetchCertificates(page); }}
      />

      <RescheduleDialog
        visible={rescheduleDialog.visible}
        certId={rescheduleDialog.certId}
        initialDateTime={rescheduleDialog.initialDateTime}
        insuredDatabaseId={insuredDatabaseId}
        onHide={closeRescheduleDialog}
        onSuccess={() => { closeRescheduleDialog(); fetchCertificates(page); }}
      />

      <Dialog
        header="Certificate Preview"
        visible={previewDialog.visible}
        style={{ width: '50vw', height: '90vh' }}
        onHide={() => setPreviewDialog({ visible: false, url: null, loading: false })}
        maximizable
      >
        {previewDialog.loading ? (
          <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
            <ProgressSpinner />
          </div>
        ) : previewDialog.url ? (
          <iframe
            src={previewDialog.url}
            style={{ width: '100%', height: '75vh', border: 'none' }}
            title="Certificate preview"
          />
        ) : (
          <div style={{ textAlign: 'center', padding: '20px' }}>
            <p>Unable to load preview</p>
          </div>
        )}
      </Dialog>
    </div>
  );
};

export default SentCertificatesPanel;
